import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, OverlayTrigger } from "react-bootstrap";
import {
  AddNode,
  ClearFilter,
  FilterIcon,
  RemoveIcon,
} from "../../../assets/images";
import { MaterialReactTable } from "material-react-table";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Button, Tooltip, CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AlertTemplateMessage from "../../Configuration/AlertTemplate/AlertTemplateMessage";
import { MultiSelect } from "react-multi-select-component";
import Swal from "sweetalert2";
import filtersConfig from "../Filters";
import {
  getAllFilters,
  getAllZoneList,
  getPreOnBoardedDeviceList,
  updatePreOnBoardedStatus,
  clearStagingDevices,
} from "../../../services/configurationServices";
import { SuccessToast } from "../../../CustomHooks/SuccessToast";
import { ErrorToast } from "../../../CustomHooks/ErrorToast";
import InfoIcon from '@mui/icons-material/Info';


const OnvifDiscoverDevices = ({ showSuccessToast }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const wrapperRef = useRef(null);
  const [filterValues, setFilterValues] = useState({});
  const [filterData, setFilterData] = useState(null);
  const [discoverDeviceData, setDiscoverDeviceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [rowSelection, setRowSelection] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Enhanced configuration with extensible field mappings
  const getPageConfiguration = () => {
    const pageConfig = filtersConfig.find(
      (config) => config.pagename === "DiscoverDevicesConfiguration",
    );
    if (!pageConfig) {
      console.warn("DiscoverDevicesConfiguration not found in filters config");
      return { filters: {}, fieldMapping: {}, displayConfig: {} };
    }
    return pageConfig;
  };

  const pageConfig = getPageConfiguration();
  const pageFilters = pageConfig.filters;

  // Dynamic field mapping configuration - can be moved to external config
  const defaultFieldMapping = {
    Customer: "ClientName",
    State: "statename",
    City: "City",
    Site: "SiteName",
    Zone: "ZoneName",
    Interface: "InterfaceName",
    Device: "DeviceName",
  };

  // Use configured field mapping or fall back to default
  const fieldMapping = pageConfig.fieldMapping || defaultFieldMapping;

  // Get the actual field name from configuration (with fallback to default mapping)
  const getFieldName = (filterKey) => {
    const filterConfig = pageFilters[filterKey];
    if (filterConfig && filterConfig.fieldName) {
      return filterConfig.fieldName;
    }
    return fieldMapping[filterKey] || filterKey;
  };

  // Get placeholder text from configuration
  const getPlaceholderText = (filterKey, filterConfig) => {
    if (filterConfig.placeholder) {
      return filterConfig.placeholder;
    }
    return `Select ${filterConfig.label}${filterConfig.mandatory ? "*" : ""}`;
  };

  // Excel-like Filter Utility - filters options based on other active selections
  const getFilteredOptions = (filterKey, optionKey, labelKey) => {
    if (!filterData || !filterData.Data) {
      return [];
    }

    // Build a filter object with only selected values for each filter (excluding current filter)
    const activeSelections = {};
    Object.keys(pageFilters).forEach((key) => {
      const selected = filterValues[key];
      if (key !== filterKey && Array.isArray(selected) && selected.length > 0) {
        // For State filter, we need to use the statemasterid for comparison
        if (key === "State") {
          activeSelections[key] = selected.map((item) =>
            typeof item === "object" ? item.value : item,
          );
        } else {
          activeSelections[key] = selected.map((item) =>
            typeof item === "object" ? item.value : item,
          );
        }
      }
    });

    // Filter data based on all other active selections
    const filtered = filterData.Data.filter((item) => {
      return Object.entries(activeSelections).every(([key, selectedVals]) => {
        // Use the correct field for comparison
        let compareField;
        if (key === "Customer") {
          compareField = "ClientId";
        } else if (key === "State") {
          compareField = "statemasterid";
        } else if (key === "City") {
          compareField = "City";
        } else if (key === "Site") {
          compareField = "SiteID";
        } else {
          compareField = getFieldName(key);
        }
        return selectedVals.includes(item[compareField]);
      });
    });

    // Get unique options for this filter
    const seen = new Set();
    return filtered
      .filter((item) => {
        const val = item[optionKey];
        if (!val || seen.has(val)) return false;
        seen.add(val);
        return true;
      })
      .map((item) => ({
        label: item[labelKey],
        value: item[optionKey],
      }))
      .sort((a, b) => {
        // Sort options in ascending order by label
        return a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
  };

  // Create options from API filterData with Excel-like filtering - now fully dynamic
  const getFilterOptions = (filterKey) => {
    if (!filterData || !filterData.Data) {
      return [];
    }

    let optionKey, labelKey;

    if (filterKey === "Customer") {
      // For Customer, use ClientId as value and ClientName as label
      optionKey = "ClientId";
      labelKey = "ClientName";
    } else if (filterKey === "State") {
      // For State, use statemasterid as value and statename as label
      optionKey = "statemasterid";
      labelKey = "statename";
    } else if (filterKey === "City") {
      // For City, use City as both value and label
      optionKey = "City";
      labelKey = "City";
    } else if (filterKey === "Site") {
      // For Site, use SiteID as value and SiteName as label
      optionKey = "SiteID";
      labelKey = "SiteName";
    } else {
      const fieldName = getFieldName(filterKey);
      optionKey = fieldName;
      labelKey = fieldName;
    }

    return getFilteredOptions(filterKey, optionKey, labelKey);
  };

  const handleFilterChange = (filterKey, value) => {
    if (filterKey === "Site" && value.length > 0) {
      setSelectedSite(value[0]);
    }
    setFilterValues((prev) => {
      const newValues = {
        ...prev,
        [filterKey]: value,
      };

      // Smart dependency clearing - clear dependent filters when parent changes
      const filterConfig = pageFilters[filterKey];
      if (filterConfig) {
        // Find and clear all dependent filters
        Object.entries(pageFilters).forEach(([key, config]) => {
          if (config.parentFilter === filterKey) {
            newValues[key] = [];
            // Recursively clear dependencies
            const clearDependencies = (parentKey) => {
              Object.entries(pageFilters).forEach(([depKey, depConfig]) => {
                if (depConfig.parentFilter === parentKey) {
                  newValues[depKey] = [];
                  clearDependencies(depKey);
                }
              });
            };
            clearDependencies(key);
          }
        });
      }

      return newValues;
    });

    // Only reset table state when mandatory filters are changed
    const filterConfig = pageFilters[filterKey];
    if (filterConfig && filterConfig.mandatory) {
      // Reset table state when mandatory filters change
      setDiscoverDeviceData([]);
      setFiltersApplied(false);
    }
  };

  // Apply filters function - now calls API and stores response
  const handleApplyFilters = async () => {
    if (!isMandatoryFiltersMissing()) {
      setLoading(true);
      setFiltersApplied(true);
      try {
        // Get logged in user ID
        let loggedInUser = JSON.parse(localStorage.getItem('user'));
        const UserID = loggedInUser?.Id;

        // Prepare filter parameters from selected values
        let CustomerID = null;
        let SiteID = null;

        if (filterValues["Customer"] && filterValues["Customer"].length > 0) {
          CustomerID = filterValues["Customer"][0]?.value || filterValues["Customer"][0];
        }
        
        if (filterValues["Site"] && filterValues["Site"].length > 0) {
          SiteID = filterValues["Site"][0]?.value || filterValues["Site"][0];
        }

        const filterParams = {
          UserID: UserID,
          CustomerID: CustomerID,
          SiteID: SiteID
        };

        const response = await getPreOnBoardedDeviceList(filterParams);
        if (response && response.Data && Array.isArray(response.Data)) {
          setDiscoverDeviceData(response.Data);
        } else {
          setDiscoverDeviceData([]);
          console.warn("No device data received:", response);
        }
      } catch (error) {
        console.error("Error loading devices:", error);
        setDiscoverDeviceData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const response = await getAllFilters({ Entity: "Interface" });
      setFilterData(response);
    } catch (error) {
      console.error("Error loading Device filters:", error);
      setFilterData(null);
    }
  };

  // Enhanced validation with better error messaging
  const isMandatoryFiltersMissing = () => {
    return Object.entries(pageFilters).some(([filterKey, filterConfig]) => {
      return (
        filterConfig.mandatory &&
        (!filterValues[filterKey] || filterValues[filterKey].length === 0)
      );
    });
  };

  // Get missing mandatory filters for better user feedback
  const getMissingMandatoryFilters = () => {
    return Object.entries(pageFilters)
      .filter(([filterKey, filterConfig]) => {
        return (
          filterConfig.mandatory &&
          (!filterValues[filterKey] || filterValues[filterKey].length === 0)
        );
      })
      .map(([filterKey, filterConfig]) => filterConfig.label);
  };

  // Enhanced filter button tooltip
  const getFilterButtonTooltip = () => {
    const missingFilters = getMissingMandatoryFilters();
    console.log(missingFilters)
    if (missingFilters.length > 0) {
      return `Please select: ${missingFilters.join(", ")}`;
    }
    return "Apply Filter";
  };

  // Clear all filters function
  const handleClearAllFilters = () => {
    setFilterValues({});
    setDiscoverDeviceData([]);
    setFiltersApplied(false);
    setGlobalFilter("");
    setRowSelection({});
  };

  // Handle global filter change with pagination reset
  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
  };

  useEffect(() => {
    if (wrapperRef.current) {
      const labelEl = wrapperRef.current.querySelector(
        ".dropdown-heading .dropdown-heading-value",
      );
      if (labelEl) {
        const firstFilterKey = Object.keys(pageFilters)[0];
        const firstSelection = filterValues[firstFilterKey];
        const selectedLabel = firstSelection?.[0]?.label || "";
        const isTruncated = labelEl.scrollWidth > labelEl.clientWidth;
        labelEl.setAttribute("title", isTruncated ? selectedLabel : "");
      }
    }
  }, [filterValues]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "DeviceName",
        header: "Device Name",
        size: 230,
      },
      {
        accessorKey: "DeviceIPAddress",
        header: "IP Address",
        size: 200,
      },
      {
        accessorKey: "DevicePort",
        header: "Port",
        size: 80,
      },
      {
        accessorKey: "DeviceUserName",
        header: "Username",
        size: 200,
      },
      {
        accessorKey: "DevicePassword",
        header: "Password",
        size: 200,
        Cell: ({ cell }) => (
          <span>••••••••</span>
        )
      },
      {
        accessorKey: "StageStatus",
        header: "Status",
        size: 200,
      },
      {
        accessorKey: "StatusMessage",
        header: "Message",
        size: 350,
      }
    ],
    [],
  );
  // Helper function to get selected row IDs
  const getSelectedRowIds = () => {
    return Object.keys(rowSelection).filter(
      (key) => rowSelection[key] === true
    );
  };

  // Helper function to check if any rows are selected
  const hasSelectedRows = () => {
    return getSelectedRowIds().length > 0;
  };

  const handleDeleteDevice = async () => {
    const selectedIds = getSelectedRowIds();

    if (selectedIds.length === 0) {
      ErrorToast("Please select at least one device to delete.");
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteDevices = async () => {
    try {
      setShowDeleteConfirm(false);
      const selectedIds = getSelectedRowIds();
      const loggedInUser = JSON.parse(localStorage.getItem('user'));
      const UserID = loggedInUser?.Id;

      if (!UserID) {
        ErrorToast("Unable to retrieve user information.");
        return;
      }

      // Extract numeric IDs from selected row keys
      const stgDeviceIds = selectedIds.map((id) => parseInt(id, 10));

      const payload = {
        STGDeviceonboardingID: stgDeviceIds,
        UserID: UserID,
      };

      const response = await clearStagingDevices(payload);

      if (response && response.Status === "success") {
        showSuccessToast("Devices deleted successfully");
        // Reset selection
        setRowSelection({});
        // Refresh device list
        handleApplyFilters();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.Message || "Failed to delete devices",
        });
      }
    } catch (error) {
      console.error("Error deleting devices:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while deleting devices",
      });
    }
  };

  const handleDiscover = async () => {
    // Get selected row IDs
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key] === true
    );

    if (selectedIds.length === 0) {
      ErrorToast("Please select at least one device to discover.");
  
      return;
    }

    try {
      // Create array of STGDeviceonboardingID from selected rows
      const selectedDevices = discoverDeviceData.filter((device) =>
        selectedIds.includes(String(device.STGDeviceonboardingID))
      );

      const payload = {
        STGDeviceonboardingID: selectedDevices.map((device) => device.STGDeviceonboardingID),
        StatusMessage:"Discovery Initiated"
      };

      const response = await updatePreOnBoardedStatus(payload);

      if (response && response.Status === "success") {
        // I want refresh icon to show in my message
        // showSuccessToast("Devices discovered successfully.")
        // showSuccessToast("Click the Refresh Button to check the latest status of all discovered devices.")
        // Reset selection
        // setRowSelection({});
        // Refresh device list
        handleApplyFilters();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.Message || "Failed to discover devices",
        });
      }
    } catch (error) {
      console.error("Error discovering devices:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while discovering devices",
      });
    }
  };
  return (
    <>
      <div className="p-3">
        <div className="SiteMainContainer SiteTableMainDiv">
          <div className="DashboardFilterMainDiv">
            <div className="RefreshText" style={{ display: "flex", alignItems: "center" }}>
              {Object.entries(pageFilters)
                .sort(([, a], [, b]) => a.sequence - b.sequence)
                .map(([filterKey, filterConfig]) => (
                  <div key={filterKey} className="inputTypeLabelContainer">
                    <label className="formFieldLabels">
                      {filterConfig.label}
                      {filterConfig.mandatory && (
                        <span style={{ color: "red" }}>*</span>
                      )}
                    </label>
                    <div ref={wrapperRef}>
                      <MultiSelect
                        key={filterValues[filterKey]?.[0]?.value}
                        options={getFilterOptions(filterKey)}
                        value={filterValues[filterKey] || []}
                        onChange={(value) =>
                          handleFilterChange(
                            filterKey,
                            value.length > 1
                              ? [value[value.length - 1]]
                              : value,
                          )
                        }
                        labelledBy={`Select ${filterConfig.label}`}
                        hasSelectAll={false}
                        isCreatable={false}
                        disableSearch={false}
                        className="SiteConfigSingleSelectDropdownContainer"
                        overrideStrings={{
                          selectSomeItems:
                            filterValues[filterKey] &&
                            filterValues[filterKey][0] &&
                            filterValues[filterKey][0].label
                              ? filterValues[filterKey][0].label
                              : getPlaceholderText(filterKey, filterConfig),
                          allItemsAreSelected:
                            filterValues[filterKey] &&
                            filterValues[filterKey][0] &&
                            filterValues[filterKey][0].label
                              ? filterValues[filterKey][0].label
                              : getPlaceholderText(filterKey, filterConfig),
                        }}
                      />
                    </div>
                  </div>
                ))}
              <div className="HMFilterDiv">
                <Tooltip title={getFilterButtonTooltip()} placement="bottom">
                  <button
                    className="IconsButtonDiv"
                    name="filterBtn"
                    disabled={isMandatoryFiltersMissing() || loading}
                    style={{
                      opacity: isMandatoryFiltersMissing() || loading ? 0.5 : 1,
                      cursor:
                        isMandatoryFiltersMissing() || loading
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={handleApplyFilters}
                  >
                    <img
                      src={FilterIcon}
                      className="camera-icon-height"
                      alt="Apply filter icon"
                    />
                  </button>
                </Tooltip>
                <Tooltip title="Clear Filter" placement="bottom">
                  <button
                    className="IconsButtonDiv"
                    name="clearFilterBtn"
                    disabled={loading}
                    style={{
                      opacity: loading ? 0.5 : 1,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                    onClick={handleClearAllFilters}
                  >
                    <img
                      src={ClearFilter}
                      className="camera-icon-height"
                      alt="Clear filter icon"
                    />
                  </button>
                </Tooltip>
              </div>
              <div className='OnvifAlertMessage'  style={{
                 width: "auto", margin:"0 0 0 auto",
                background: "lightyellow",borderColor: "#ff9800",borderStyle: "solid",borderWidth: "1px" }} >
                <InfoIcon color="warning" style={{ marginRight: "5px" }} />
                <h5  style={{color: "#ed6c02"}}>Click the <RefreshIcon /> Button to check the latest status of all discovered devices.</h5>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Refresh" placement="bottom">
                  <IconButton
                    className="m-1 ConfigBelowButtons IconsButtonDiv"
                    name="refreshFilterBtn"
                    disabled={isMandatoryFiltersMissing() || loading}
                    onClick={handleApplyFilters}
                    style={{
                      opacity: isMandatoryFiltersMissing() || loading ? 0.5 : 1,
                      cursor:
                        isMandatoryFiltersMissing() || loading
                          ? "not-allowed"
                          : "pointer",
                        color:"white"
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="SiteMainHeightDiv">
            <div
              className="col-lg-12 m-0 TabulerReportMainDiv MatReactTableDiv"
              style={{ height: "calc(100vh - 300px)" }}
            >
              <div className="card-body p-0">
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="400px"
                    flexDirection="column"
                  >
                    <CircularProgress size={60} thickness={4} />
                    <Box mt={2}>
                      <span style={{ fontSize: "16px", color: "#666" }}>
                        Loading device data...
                      </span>
                    </Box>
                  </Box>
                ) : discoverDeviceData.length === 0 ? (
                  <div
                    style={{
                      width: "100%",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <AlertTemplateMessage
                      message={
                        !filtersApplied || isMandatoryFiltersMissing()
                          ? "Please select mandatory filters and click Apply Filter to load device data."
                          : "No device found for the selected filters. Please add a device"
                      }
                    />
                  </div>
                ) : (
                  <MaterialReactTable
                    columns={columns}
                    data={discoverDeviceData}
                    columnOrder={['mrt-row-select', 'DeviceName', 'DeviceIPAddress', 'DevicePort', 'DeviceUserName', 'DevicePassword', 'StageStatus', 'StatusMessage', 'mrt-row-expand']}
                    enableGlobalFilter={true}
                    enableColumnFilters={true}
                    enableStickyHeader
                    enableColumnResizing={true}
                    globalFilterFn="contains"
                    columnResizeMode="onEnd"
                    enableColumnOrdering={true}
                    enableRowSelection={true}
                    displayColumnDefOptions={{
                      "mrt-row-select": {
                        muiTableHeadCellProps: {
                          sx: {
                            width: 40,
                            minWidth: 40,
                            maxWidth: 40,
                            padding: '0 4px',
                          },
                        },
                        muiTableBodyCellProps: {
                          sx: {
                            width: 40,
                            minWidth: 40,
                            maxWidth: 40,
                            padding: '0 4px',
                          },
                        },
                      },
                      "mrt-row-expand": {
                        muiTableHeadCellProps: {
                          sx: {
                            width: 40,
                            minWidth: 40,
                            maxWidth: 40,
                            padding: '0 4px',
                          },
                        },
                        muiTableBodyCellProps: {
                          sx: {
                            width: 40,
                            minWidth: 40,
                            maxWidth: 40,
                            padding: '0 4px',
                          },
                        },
                      },
                    }}
                    muiTableHeadCellProps={{
                      className: "myHeaderSelectAll",
                    }}
                    enableHiding={true}
                    initialState={{ showColumnFilters: true }}
                    getRowId={(row) => String(row.STGDeviceonboardingID)}
                    state={{
                      globalFilter: globalFilter,
                      pagination: pagination,
                      rowSelection: rowSelection,
                    }}
                    onGlobalFilterChange={handleGlobalFilterChange}
                    onPaginationChange={setPagination}
                    onRowSelectionChange={setRowSelection}
                    muiTableContainerProps={{
                      className: "MaterialTableJs myTableContainer",
                      sx: {
                        maxHeight: "100%",
                        overflow: "auto",
                      },
                    }}
                    muiTableHeadProps={{
                      className: "myHeader",
                      sx: {
                        display: "table-header-group",
                      },
                    }}
                    renderTopToolbarCustomActions={({ table }) => (
                      <div className="d-flex justify-content-center align-items-center">
                        <h4 style={{ margin: 0 }}>
                          List of Devices{" "}
                          {discoverDeviceData.length > 0 &&
                            `(${discoverDeviceData.length} records)`}
                        </h4>
                      </div>
                    )}
                  />
                )}

              </div>
            </div>
          </div>
            <div className='mb-2 mt-2 mr-2 text-right'>
                <Button 
                    className='m-1 mr-1 ConfigBelowButtons' 
                    color="error" 
                    variant="contained"
                    onClick={handleDeleteDevice}
                    disabled={!hasSelectedRows() || loading}
                >
                    Delete Device
                </Button>
                
                <Button 
                    className='m-1 ConfigBelowButtons' 
                    variant="contained" 
                    onClick={handleDiscover}
                    disabled={!hasSelectedRows() || loading}
                >
                    Discover
                </Button>
            </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete {getSelectedRowIds().length} selected device(s)? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteDevices} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OnvifDiscoverDevices;
